import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'
import _ from 'lodash'
import { useNavigate, useParams } from 'react-router-dom'
import { MainLayout } from 'views/layouts'

import { Container, InputsContainer, InputContainer, ErrorContainer, Error } from './styled'
import Input from 'views/components/UI/TextInput'
import Select from 'views/components/UI/Select'
import DatePicker from 'views/components/UI/Datepicker'
import { Button } from 'views/components/UI/Buttons'

import { generalDataSelector } from 'ducks/auth'
import { tokenSelector } from 'ducks/user'
import { BACK_PAGE } from '../constants'
import createNewInventory from 'services/inventory/new'
import { useNotification } from 'lib/context/notification.context'
import { BtnGoBack, ContainerRow } from 'views/components/Modal/styled'
import dayjs from 'dayjs'
import axios from 'axios'
import { isUnlimitedSubscription } from './inventory-creation'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('El nombre del inventario es requerido'),
  type: Yup.string().required('Debe seleccionar un tipo de inventario'),
  credits: Yup.number().when('type', {
    is: isUnlimitedSubscription,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.integer('La cantidad debe ser un número entero').min(1, 'La cantidad debe ser mayor que cero').required('Este campo es requerido'),
  }),
  expirationDate: Yup.date()
    .required('La fecha de vencimiento es requerida')
    .test('is-future-date', 'La fecha debe ser igual o posterior a hoy', function (value) {
      if (!value) return false
      const selectedDate = dayjs(value).startOf('day')
      const currentDate = dayjs().startOf('day')
      return selectedDate.isAfter(currentDate) || selectedDate.isSame(currentDate, 'day')
    }),
})

function Index() {
  const { getWarning, getSuccess } = useNotification()
  const navigate = useNavigate()
  const { data } = useSelector(generalDataSelector)
  const token = useSelector(tokenSelector)
  const params = useParams()
  const [idCompany, setIdCompany] = useState<number | null>(null)
  const [optionsType, setOptionsType] = useState<any>([])

  useEffect(() => {
    if (data.hasOwnProperty('subscription')) {
      const options = _.map(data.subscription, (item: any) => {
        return {
          value: String(item.id),
          label: item.name,
        }
      })
      setOptionsType(options)
    } else {
      setOptionsType([])
    }


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const companyId = params.companyId
    setIdCompany(parseInt(companyId ?? '0'))

  }, [params])

  interface IBodyData {
    expirationDate: string | null
    name: string
    type: string
    credits: string
  }

  async function handleCreateInventory(values: IBodyData) {
    const { expirationDate, name, type, credits } = values
    if (!idCompany || !expirationDate) {
      getWarning('No se pudo identificar la compañía o la fecha de vencimiento')
      return
    }
    const isUnlimited = isUnlimitedSubscription(type)
    const bodyData = {
      subscriptionId: parseInt(type),
      companyId: idCompany,
      expirationDate: expirationDate,
      name,
      credits: isUnlimited ? 1000000000 : parseInt(credits),
    }

    try {
      const res = await createNewInventory(token, bodyData)
      if (res.status === 'success') {
        getSuccess('Inventario creado correctamente')
        navigate(BACK_PAGE)
      } else {
        getWarning(res.message || 'No se pudo crear el inventario')
      }
    } catch (error: unknown) {
      const message = axios.isAxiosError(error) ? error.response?.data?.message : null
      getWarning(message || 'No se pudo crear el inventario. Inténtalo de nuevo')
    }
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      type: '',
      credits: '0',
      expirationDate: null,
    },
    validationSchema: validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: handleCreateInventory,
  })

  return (
    <MainLayout>
      <form onSubmit={formik.handleSubmit}>
        <Container>
          <h1>Nuevo inventario</h1>
          <InputsContainer>
            <InputContainer>
              <Input
                type="text"
                name="name"
                label="Nombre"
                placeholder="Nombre del inventario"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
              />
              {formik.touched.name && formik.errors.name ? (
                <ErrorContainer>
                  {' '}
                  <Error>{formik.errors.name}</Error>{' '}
                </ErrorContainer>
              ) : null}
            </InputContainer>
            <InputContainer>
              <Select
                hideNeverChoice={true}
                label="Tipo"
                options={optionsType || []}
                onChange={(e: string) => {
                  formik.setFieldValue('type', String(e))
                }}
                value={formik.values.type}
              />
              {formik.touched.type && formik.errors.type ? (
                <ErrorContainer>
                  {' '}
                  <Error>{formik.errors.type}</Error>{' '}
                </ErrorContainer>
              ) : null}
            </InputContainer>
            {!isUnlimitedSubscription(formik.values.type) && (
              <InputContainer>
                <Input
                  type="number"
                  name="credits"
                  label="Cantidad de pruebas"
                  placeholder="Cantidad de pruebas"
                  onChange={(e: any) => {
                    const value = e.target.value
                    // Solo permitir números enteros positivos
                    if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
                      formik.handleChange('credits')(e)
                    }
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.credits}
                />
                {formik.touched.credits && formik.errors.credits ? (
                  <ErrorContainer>
                    {' '}
                    <Error>{formik.errors.credits}</Error>{' '}
                  </ErrorContainer>
                ) : null}
              </InputContainer>
            )}
            <InputContainer>
              <DatePicker
                useISO8601={true}
                label="Fecha de vencimiento"
                minDate={new Date()} // Deshabilitar fechas anteriores a hoy
                onChange={(e) => {
                  // Primero verificamos si la fecha es válida
                  if (!e || !dayjs(e).isValid()) {
                    formik.setFieldValue('expirationDate', null);
                    return;
                  }

                  const currentDate = new Date();
                  const today = dayjs(currentDate).startOf('day');
                  const selectedDate = dayjs(e).startOf('day');

                  // Verificar si la fecha es anterior a hoy y mostrar notificación
                  if (selectedDate.isBefore(today)) {
                    getWarning('No se puede seleccionar una fecha anterior a la actual');
                    formik.setFieldValue('expirationDate', null);
                  } else {
                    formik.setFieldValue('expirationDate', e);
                  }

                  formik.validateField('expirationDate');
                }}
              />

              {formik.touched.expirationDate && formik.errors.expirationDate ? (
                <ErrorContainer>
                  <Error>{formik.errors.expirationDate}</Error>
                </ErrorContainer>
              ) : null}
            </InputContainer>
          </InputsContainer>
          <ContainerRow>
            <BtnGoBack onClick={() => navigate(-1)}>Cancelar</BtnGoBack>
            <Button
              disabled={!formik.isValid || !formik.values.name || !formik.values.type || !formik.values.expirationDate}
              type="submit"
            >
              Guardar
            </Button>
          </ContainerRow>
        </Container>
      </form>
    </MainLayout>
  )
}

export default Index
